import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { ArrowRight, TrendingUp, Users, Zap, Shield, BarChart3, Trophy, Play, ChevronRight, Mail, Gavel, Code2, Brain, Activity, HelpCircle, Target, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { intelligenceAPI } from '../services/api';
import logoImage from '../assets/logo.png';
import apkFile from '../assets/ipltrade.apk';
import './LandingPage.css';

const LandingPage = () => {
  const navigate = useNavigate();
  const [intelLoading, setIntelLoading] = useState(true);
  const [intelError, setIntelError] = useState('');
  const [lastMatchReport, setLastMatchReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(true);

  // Live market animation — updates every 1.8s
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => (t + 1) % 1000), 1800);
    return () => clearInterval(id);
  }, []);

  const livePlayers = useMemo(() => [
    { initials:'VK', name:'Virat Kohli',    team:'RCB · BAT', bg:'linear-gradient(135deg,#dc2626,#ff6b35)', base:234, phase:0.0 },
    { initials:'MS', name:'MS Dhoni',       team:'CSK · WK',  bg:'linear-gradient(135deg,#1d4ed8,#7c3aed)', base:198, phase:1.2 },
    { initials:'RO', name:'Rohit Sharma',   team:'MI · BAT',  bg:'linear-gradient(135deg,#0369a1,#06b6d4)', base:215, phase:2.1 },
    { initials:'JB', name:'Jasprit Bumrah', team:'MI · BWL',  bg:'linear-gradient(135deg,#065f46,#10b981)', base:189, phase:0.8 },
  ].map(p => {
    const delta  = Math.sin(tick * 0.6 + p.phase) * 5 + Math.sin(tick * 1.3 + p.phase) * 2;
    const price  = Math.round(p.base + delta);
    const pct    = (Math.abs(delta) / p.base * 100).toFixed(1);
    return { ...p, price, pct, positive: delta >= 0 };
  }), [tick]);

  // Portfolio: 8 players at realistic virtual prices (₹2000 starting credits)
  // VK(234)×2 + MS(198)×2 + RO(215)×1 + JB(189)×1 + SKY(175)×1 + HH(162)×1 = 1,566
  const livePortfolio = useMemo(() => {
    const d = livePlayers.reduce((s, p) => s + (p.price - p.base), 0);
    return { total: 1566 + Math.round(d), pnl: Math.round(d) };
  }, [livePlayers]);

  const tickerItems = useMemo(() => [
    ...livePlayers.map(p => ({ label: p.initials, price: p.price, pct: p.pct, up: p.positive })),
    { label:'SKY', price:175, pct:'2.1', up:true  },
    { label:'HH',  price:162, pct:'0.8', up:false },
    { label:'DK',  price:155, pct:'4.5', up:true  },
    { label:'SR',  price:138, pct:'0.4', up:true  },
    { label:'AK',  price:148, pct:'1.1', up:true  },
    { label:'PP',  price:126, pct:'2.3', up:false },
  ], [livePlayers]);

  // Upcoming matches from intel API
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [totalMatches, setTotalMatches] = useState(74);
  const [intelSource, setIntelSource] = useState('schedule');
  useEffect(() => {
    let active = true;

    const pickMatches = (matches = []) => {
      const today = new Date().toISOString().split('T')[0];
      const futureMatches = matches.filter(m => m.date >= today && m.status !== 'completed');
      if (futureMatches.length > 0) return futureMatches.slice(0, 2);
      if (matches.length > 0) return matches.slice(0, 2);
      return [];
    };

    const applyMatches = (matches = []) => {
      if (!active || !Array.isArray(matches)) return;
      setTotalMatches(Math.max(matches.length, 74));
      setUpcomingMatches(pickMatches(matches));
      setIntelError(matches.length ? '' : 'No match intel available right now.');
      setIntelLoading(false);
    };

    intelligenceAPI.schedule()
      .then(({ data }) => {
        if (active) setIntelSource(data?.source || 'schedule');
        applyMatches(data?.matches || []);
      })
      .catch(() => {
        fetch('https://ipltrade.in/api/intelligence/schedule')
          .then(r => r.json())
          .then(data => {
            if (active) setIntelSource(data?.source || 'schedule');
            applyMatches(data?.matches || []);
          })
          .catch(() => {
            if (!active) return;
            setIntelError('Unable to load match intel right now.');
            setIntelLoading(false);
          });
      });

    return () => {
      active = false;
    };
  }, []);

  // Fetch last completed match report for showcase
  useEffect(() => {
    let active = true;

    const fetchLastReport = async () => {
      try {
        const { data } = await intelligenceAPI.schedule();
        if (!active) return;
        
        const matches = data?.matches || [];
        const completedMatches = matches.filter(m => m.status === 'completed' || m.status?.toLowerCase()?.includes('completed'));
        
        if (completedMatches.length > 0) {
          const lastMatch = completedMatches[completedMatches.length - 1];
          try {
            const reportData = await intelligenceAPI.getIntelligence(lastMatch.matchNo);
            if (active) {
              setLastMatchReport({ match: lastMatch, data: reportData.data });
            }
          } catch (e) {
            // Report fetch failed, just show the match info
            if (active) {
              setLastMatchReport({ match: lastMatch, data: null });
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch last report:', err);
      } finally {
        if (active) {
          setReportLoading(false);
        }
      }
    };

    fetchLastReport();
    return () => {
      active = false;
    };
  }, []);

  const handleRegister = () => navigate('/auth?mode=register');
  const handleLogin = () => navigate('/auth?mode=login');

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <Helmet>
        <title>IPLTrade — Trade Cricket Player Stocks | Fantasy IPLTrade Market Game</title>
        <meta name="description" content="IPLTrade is India's fantasy IPLTrade market game with match prediction insights, player trading strategy, analytics and post match analysis." />
        <meta name="keywords" content="ipl match prediction, ipl player trading, ipl analytics, ipl match analysis, fantasy cricket prediction, best players to buy in ipl today, ipl match prediction after toss, ipl player performance analysis, ipl trading game india" />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="author" content="IPLTrade" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="IPLTrade — Trade Cricket Player Stocks | Fantasy IPLTrade Market" />
        <meta property="og:description" content="Experience India's first fantasy IPLTrade market. Trade top cricket stars like stocks." />
        <meta property="og:image" content="https://ipltrade.in/og-image.png" />
        <meta property="og:url" content="https://ipltrade.in/" />
        <meta property="og:site_name" content="IPLTrade" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="IPLTrade — Fantasy IPLTrade Market" />
        <meta name="twitter:description" content="Trade cricket players like stocks. Buy, sell, predict and win prizes." />
        <meta name="twitter:image" content="https://ipltrade.in/og-image.png" />

        {/* Additional Meta Tags */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="theme-color" content="#ff6b35" />
        <link rel="canonical" href="https://ipltrade.in/" />

        {/* JSON-LD Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "IPLTrade",
            "description": "India's first fantasy IPLTrade market gaming platform",
            "url": "https://ipltrade.in",
            "applicationCategory": "GameApplication",
            "operatingSystem": "Web",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "INR",
              "availability": "https://schema.org/InStock"
            },
            "creator": {
              "@type": "Organization",
              "name": "IPLTrade",
              "url": "https://ipltrade.in"
            }
          })}
        </script>
      </Helmet>

      <div className="landing-page">
        
        {/* Navigation */}
        <nav className="landing-navbar">
          <div className="navbar-container">
            <div className="navbar-logo">
              <img src={logoImage} alt="IPLTrade" className="navbar-logo-image" />
            </div>
            <div className="navbar-links" style={{display: window.innerWidth > 768 ? 'flex' : 'none'}}>
              <button onClick={() => scrollToSection('features')} className="nav-link">Features</button>
              <button onClick={() => scrollToSection('how-it-works')} className="nav-link">How It Works</button>
              <button onClick={() => scrollToSection('faq')} className="nav-link">FAQ</button>
            </div>
            <div className="nav-buttons">
              <a href={apkFile} download="IPLTrade.apk" className="btn-download-app">
                <Download size={15} />
                <span className="download-label-desktop">Download App</span>
                <span className="download-label-mobile">Get App</span>
              </a>
              <button onClick={handleLogin} className="btn-login">Login</button>
              <button onClick={handleRegister} className="btn-register">Register Free</button>
            </div>
          </div>
        </nav>

        {/* Live Ticker Strip */}
        <div className="ticker-wrap">
          <div className="ticker-track">
            {[...tickerItems, ...tickerItems].map((item, i) => (
              <span key={i} className="ticker-item">
                <span className="t-name">{item.label}</span>
                <span className="t-price">₹{item.price}</span>
                <span className={`t-change ${item.up ? 'positive' : 'negative'}`}>
                  {item.up ? '▲' : '▼'}{item.pct}%
                </span>
              </span>
            ))}
          </div>
        </div>

        {/* Hero Section */}
        <section className="hero-section">
          {/* Left Content */}
          <div>
            <div style={{marginBottom: '32px'}}>
              <div className="hero-badge">🏏 100% Free · No Real Money · Pure Strategy</div>
              <h1>
                Trade Cricket Players with <span>IPLTrade</span>
              </h1>
              <p style={{marginTop: '16px'}}>
                India's first fantasy IPLTrade market. Buy and sell virtual player shares based on live match performance. Zero investment. Pure cricket intelligence.
              </p>
            </div>

            <div className="hero-buttons">
              <button onClick={handleLogin} className="btn-primary">
                Start Trading Free <ArrowRight size={20} />
              </button>
              <button onClick={() => scrollToSection('how-it-works')} className="btn-secondary">
                <Play size={20} /> Watch Demo
              </button>
            </div>

            <div className="hero-stats">
              <div className="stat">
                <div className="stat-value">₹0</div>
                <div className="stat-label">Entry Fee</div>
              </div>
              <div className="stat">
                <div className="stat-value">{totalMatches}</div>
                <div className="stat-label">IPLTrade Season Matches</div>
              </div>
              <div className="stat">
                <div className="stat-value">AI</div>
                <div className="stat-label">Match Intel</div>
              </div>
            </div>
          </div>

          {/* Hero Dashboard Mockup */}
          <div className="hero-dashboard">
            <div className="dashboard-header">
              <div className="dashboard-title">
                <span className="dashboard-logo-dot"></span>
                IPLTrade Market
              </div>
              <span className="live-badge">● LIVE</span>
            </div>

            <div className="dashboard-chart">
              <div className="chart-label">Virat Kohli — ₹{livePlayers[0].price}</div>
              <svg viewBox="0 0 400 100" className="price-chart" preserveAspectRatio="none" style={{overflow:'visible'}}>
                <defs>
                  <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ff6b35" stopOpacity="0.35"/>
                    <stop offset="100%" stopColor="#ff6b35" stopOpacity="0"/>
                  </linearGradient>
                </defs>
                <g className="hero-chart-paths">
                  <path d="M0,85 L40,75 L80,80 L120,55 L160,62 L200,40 L240,48 L280,28 L320,35 L360,15 L400,20 L400,100 L0,100 Z"
                        fill="url(#chartGrad)"/>
                  <path d="M0,85 L40,75 L80,80 L120,55 L160,62 L200,40 L240,48 L280,28 L320,35 L360,15 L400,20"
                        stroke="#ff6b35" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
                </g>
              </svg>
              <div className={`chart-change ${livePlayers[0].positive ? 'positive' : 'negative'}`}>
                {livePlayers[0].positive ? '▲' : '▼'} {livePlayers[0].pct}% today
              </div>
            </div>

            <div className="dashboard-players">
              {livePlayers.map((p, i) => (
                <div key={i} className="player-row">
                  <div className="player-avatar" style={{background: p.bg}}>{p.initials}</div>
                  <div className="player-info">
                    <div className="player-name">{p.name}</div>
                    <div className="player-team">{p.team}</div>
                  </div>
                  <div className="player-price-col">
                    <div className="player-price">₹{p.price}</div>
                    <div className={`price-change ${p.positive ? 'positive' : 'negative'}`}>
                      {p.positive ? '▲' : '▼'} {p.pct}%
                    </div>
                  </div>
                  <button type="button" className="player-buy-btn" onClick={handleLogin}>
                    Buy
                  </button>
                </div>
              ))}
            </div>

            <div className="dashboard-footer-stats">
              <div className="folio-stat">
                <div className="folio-label">Portfolio</div>
                <div className="folio-value">₹{livePortfolio.total.toLocaleString()}</div>
              </div>
              <div className="folio-divider"></div>
              <div className="folio-stat">
                <div className="folio-label">Today's P&amp;L</div>
                <div className={`folio-value ${livePortfolio.pnl >= 0 ? 'positive' : 'negative'}`}>
                  {livePortfolio.pnl >= 0 ? '+' : ''}₹{Math.abs(livePortfolio.pnl)}
                </div>
              </div>
              <div className="folio-divider"></div>
              <div className="folio-stat">
                <div className="folio-label">Holdings</div>
                <div className="folio-value">8 Players</div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="features-section">
          <div className="section-container">
            <h2 className="section-title">Why Choose IPLTrade?</h2>
            <p className="section-subtitle">Experience the thrill of stock market trading with your favorite IPLTrade players</p>

            <div className="features-grid">
              {/* Feature 1 */}
              <div className="feature-card">
                <div className="feature-icon">
                  <TrendingUp size={24} />
                </div>
                <h3>Real-time Prices</h3>
                <p>Player prices change based on live match performance, just like stock markets</p>
              </div>

              {/* Feature 2 */}
              <div className="feature-card">
                <div className="feature-icon">
                  <Users size={24} />
                </div>
                <h3>Compete with Friends</h3>
                <p>Create private contests and compete with your cricket-loving friends</p>
              </div>

              {/* Feature 3 */}
              <div className="feature-card">
                <div className="feature-icon">
                  <Brain size={24} />
                </div>
                <h3>AI Match Intelligence</h3>
                <p>Pre-match player predictions, win probability &amp; toss impact — built into every trade decision</p>
              </div>

              {/* Feature 4 */}
              <div className="feature-card">
                <div className="feature-icon">
                  <Zap size={24} />
                </div>
                <h3>Instant Decisions</h3>
                <p>Trade anytime market is open. Quick buy, sell, and profit opportunities</p>
              </div>

              {/* Feature 5 */}
              <div className="feature-card">
                <div className="feature-icon">
                  <Shield size={24} />
                </div>
                <h3>Safe & Secure</h3>
                <p>Your data is encrypted and protected with industry-standard security</p>
              </div>

              {/* Feature 6 */}
              <div className="feature-card">
                <div className="feature-icon">
                  <Activity size={24} />
                </div>
                <h3>Live Price Engine</h3>
                <p>Prices update in real-time based on runs, wickets &amp; match events — just like a real stock exchange</p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section id="how-it-works" className="how-it-works">
          <div className="section-container">
            <h2 className="section-title">How IPLTrade Fantasy Market Works?</h2>
            <p className="section-subtitle">Simple steps to start your trading journey</p>

            <div className="steps-grid">
              {[
                { num: '1', title: 'Register Free', desc: 'Create your account in seconds', icon: Users },
                { num: '2', title: 'Get ₹2000 Credits', desc: 'Virtual credits — no real money, ever', icon: TrendingUp },
                { num: '3', title: 'Buy Players', desc: 'Pick IPLTrade stars and build your portfolio', icon: BarChart3 },
                { num: '4', title: 'Watch Prices Move', desc: 'Prices shift with every boundary, wicket & over', icon: Activity }
              ].map((step, idx) => (
                <div key={idx} style={{position: 'relative'}}>
                  <div className="step-card">
                    <div className="step-number">{step.num}</div>
                    <div className="step-icon-wrap"><step.icon size={22} /></div>
                    <h3>{step.title}</h3>
                    <p>{step.desc}</p>
                  </div>
                  {idx < 3 && (
                    <div className="step-arrow">
                      <ChevronRight size={24} />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Market Visualization */}
            <div className="market-visualization">
              <div className="market-viz-header">
                <span>IPLTrade Player Market — Live Price Action</span>
                <span className="live-badge">● LIVE</span>
              </div>
              <div className="market-viz-chart">
                <svg viewBox="0 0 800 200" className="multi-chart" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="grad1" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ff6b35" stopOpacity="0.3"/>
                      <stop offset="100%" stopColor="#ff6b35" stopOpacity="0"/>
                    </linearGradient>
                    <linearGradient id="grad2" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity="0.3"/>
                      <stop offset="100%" stopColor="#10b981" stopOpacity="0"/>
                    </linearGradient>
                    <linearGradient id="grad3" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3"/>
                      <stop offset="100%" stopColor="#3b82f6" stopOpacity="0"/>
                    </linearGradient>
                  </defs>
                  {/* Grid lines */}
                  <line x1="0" y1="50" x2="800" y2="50" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
                  <line x1="0" y1="100" x2="800" y2="100" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
                  <line x1="0" y1="150" x2="800" y2="150" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
                  {/* Vertical grid */}
                  <line x1="160" y1="0" x2="160" y2="200" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
                  <line x1="320" y1="0" x2="320" y2="200" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
                  <line x1="480" y1="0" x2="480" y2="200" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
                  <line x1="640" y1="0" x2="640" y2="200" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
                  {/* Virat Kohli line */}
                  <g className="mc-line1">
                    <path d="M0,160 L80,145 L160,150 L240,120 L320,130 L400,100 L480,110 L560,75 L640,85 L720,55 L800,60 L800,200 L0,200 Z"
                          fill="url(#grad1)"/>
                    <path d="M0,160 L80,145 L160,150 L240,120 L320,130 L400,100 L480,110 L560,75 L640,85 L720,55 L800,60"
                          stroke="#ff6b35" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
                  </g>
                  {/* MS Dhoni line */}
                  <g className="mc-line2">
                    <path d="M0,170 L80,162 L160,167 L240,145 L320,152 L400,135 L480,140 L560,120 L640,115 L720,105 L800,110 L800,200 L0,200 Z"
                          fill="url(#grad2)"/>
                    <path d="M0,170 L80,162 L160,167 L240,145 L320,152 L400,135 L480,140 L560,120 L640,115 L720,105 L800,110"
                          stroke="#10b981" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
                  </g>
                  {/* Bumrah line */}
                  <g className="mc-line3">
                    <path d="M0,180 L80,168 L160,175 L240,160 L320,165 L400,148 L480,142 L560,130 L640,125 L720,112 L800,108 L800,200 L0,200 Z"
                          fill="url(#grad3)"/>
                    <path d="M0,180 L80,168 L160,175 L240,160 L320,165 L400,148 L480,142 L560,130 L640,125 L720,112 L800,108"
                          stroke="#3b82f6" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
                  </g>
                </svg>
              </div>
              <div className="market-viz-legend">
                <span className="legend-item"><span className="legend-dot" style={{background: '#ff6b35'}}></span> Virat Kohli ↑5.2%</span>
                <span className="legend-item"><span className="legend-dot" style={{background: '#10b981'}}></span> MS Dhoni ↑3.1%</span>
                <span className="legend-item"><span className="legend-dot" style={{background: '#3b82f6'}}></span> J. Bumrah ↑8.4%</span>
              </div>
            </div>
          </div>
        </section>

        {/* Platform Facts Strip */}
        <section className="stats-section">
          <div className="section-container">
            <div className="stats-grid">
              <div className="stat-item">
                <h4>₹0</h4>
                <p>Cost to Play</p>
              </div>
              <div className="stat-item">
                <h4>{totalMatches}</h4>
                <p>IPLTrade Season Matches</p>
              </div>
              <div className="stat-item">
                <h4>10</h4>
                <p>Teams</p>
              </div>
              <div className="stat-item">
                <h4>AI</h4>
                <p>Powered Intel Engine</p>
              </div>
            </div>
          </div>
        </section>

        {/* Intel Showcase Section */}
        <section className="intel-showcase">
          <div className="section-container">
            <div className="intel-header">
              <div className="intel-badge"><Brain size={16} /> INTELLIGENCE ENGINE</div>
              <h2 className="section-title" style={{marginTop: '12px'}}>The Gem of IPLTrade — <span className="intel-highlight">Match Intel</span></h2>
              <p className="section-subtitle">Before every match, our AI engine analyses venues, player form, toss impact &amp; conditions to give you an edge. Know before you trade.</p>
            </div>

            <div className="intel-layout">
              {/* Left: Upcoming Matches from Intel API */}
              <div className="intel-mockup">
                <div className="intel-mockup-header">
                  <div className="intel-match-title">Upcoming Matches &nbsp;<span className="live-badge">● LIVE</span></div>
                  <div className="intel-match-sub">Next scheduled IPLTrade 2026 matches</div>
                </div>

                {intelLoading && (
                  <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                    Loading upcoming matches...
                  </div>
                )}

                {!intelLoading && intelError && (
                  <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                    {intelError}
                  </div>
                )}

                {!intelLoading && upcomingMatches.slice(0, 2).map((m, idx) => (
                  <div key={idx} style={{
                    margin: '14px 0',
                    background: 'rgba(255,255,255,0.03)',
                    borderRadius: 10,
                    padding: '14px 16px',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                      <span style={{ fontSize: '0.63rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>
                        Match {m.matchNo}{m.label ? ' · ' + m.label : ''}
                      </span>
                      <span style={{ fontSize: '0.63rem', color: 'var(--text-muted)' }}>
                        {m.date} · {m.time} IST
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flex: 1 }}>
                        <div style={{
                          width: 44, height: 44, borderRadius: '50%',
                          background: `${m.team1Info?.color || '#888'}22`,
                          border: `2px solid ${m.team1Info?.color || '#888'}55`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontWeight: 700, fontSize: '0.78rem', color: m.team1Info?.color || '#888',
                        }}>{m.team1}</div>
                        <span style={{ fontSize: '0.68rem', fontWeight: 600, color: 'var(--text-secondary)', textAlign: 'center' }}>
                          {m.team1Info?.name || m.team1}
                        </span>
                      </div>
                      <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-muted)', padding: '0 12px' }}>VS</div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flex: 1 }}>
                        <div style={{
                          width: 44, height: 44, borderRadius: '50%',
                          background: `${m.team2Info?.color || '#888'}22`,
                          border: `2px solid ${m.team2Info?.color || '#888'}55`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontWeight: 700, fontSize: '0.78rem', color: m.team2Info?.color || '#888',
                        }}>{m.team2}</div>
                        <span style={{ fontSize: '0.68rem', fontWeight: 600, color: 'var(--text-secondary)', textAlign: 'center' }}>
                          {m.team2Info?.name || m.team2}
                        </span>
                      </div>
                    </div>
                    <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textAlign: 'center', marginTop: 6 }}>
                      📍 {m.venue}
                    </div>
                  </div>
                ))}

                <button onClick={handleLogin} className="intel-cta-btn">
                  <Brain size={16} /> Get Full Match Intelligence
                </button>
              </div>

              {/* Right: Live stats from next two matches */}
              <div className="intel-features">
                <div className="intel-feature-item">
                  <div className="intel-feature-icon"><BarChart3 size={20} /></div>
                  <div>
                    <div className="intel-feature-title">Live Match Stats</div>
                    <div className="intel-feature-desc">Next 2 fixtures with venue, date, time and match numbers from the Intel API.</div>
                  </div>
                </div>

                {upcomingMatches.slice(0, 2).map((m, idx) => (
                  <div key={`stats-${idx}`} className="intel-feature-item" style={{ alignItems: 'flex-start' }}>
                    <div className="intel-feature-icon"><Target size={18} /></div>
                    <div style={{ width: '100%' }}>
                      <div className="intel-feature-title">{m.team1} vs {m.team2}</div>
                      <div className="intel-feature-desc" style={{ marginTop: 4 }}>
                        Match {m.matchNo}{m.label ? ` (${m.label})` : ''}
                      </div>
                      <div style={{
                        marginTop: 8,
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 8,
                        fontSize: '0.72rem',
                        color: 'var(--text-secondary)',
                      }}>
                        <div>Date: {m.date}</div>
                        <div>Time: {m.time} IST</div>
                        <div style={{ gridColumn: '1 / -1' }}>Venue: {m.venue}</div>
                      </div>
                    </div>
                  </div>
                ))}

                {(!intelLoading && !upcomingMatches.length) && (
                  <div className="intel-feature-item">
                    <div className="intel-feature-icon"><Activity size={20} /></div>
                    <div>
                      <div className="intel-feature-title">Stats unavailable right now</div>
                      <div className="intel-feature-desc">Please refresh in a moment. Source: {intelSource}.</div>
                    </div>
                  </div>
                )}

                <button onClick={handleLogin} className="btn-primary" style={{marginTop: '24px', width: '100%', justifyContent: 'center'}}>
                  Explore Intel Free <ArrowRight size={18} />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="faq-section">
          <div className="faq-container">
            <h2 className="section-title">Frequently Asked Questions</h2>
            <p className="section-subtitle">Get answers to common questions about IPLTrade</p>

            <div>
              {[
                { q: 'Is IPLTrade completely free?', a: 'Yes! IPLTrade is 100% free to play. No deposits required. You get ₹2000 virtual credits to start trading immediately.' },
                { q: 'Is there any real money involved?', a: 'No. IPLTrade is a completely virtual platform. You trade with virtual credits (₹2000 on signup). No real money, no deposits, no withdrawals. Pure strategy and cricket knowledge.' },
                { q: 'What happens when market is closed?', a: 'During market closure, you cannot trade players, but you can view your portfolio and prepare for the next trading session.' },
                { q: 'How are player prices determined?', a: 'Player prices are dynamically calculated based on live match performance (runs, wickets, catches, etc.) and market demand.' },
                { q: 'Is my data safe?', a: 'Yes, we use bank-level encryption to protect your data. All transactions are secure and your information is never shared.' },
                { q: 'Can I play with friends?', a: 'Yes! Create private contests, invite friends, and compete on leaderboards. Track performance together!' }
              ].map((faq, idx) => (
                <FAQItem key={idx} question={faq.q} answer={faq.a} />
              ))}
            </div>
          </div>
        </section>

        {/* Last Match Report Showcase (No Login Required) */}
        {!reportLoading && lastMatchReport && (
          <section className="last-match-showcase" style={{background: 'linear-gradient(135deg, rgba(255,107,53,0.08), rgba(241,196,15,0.05))', padding: '60px 20px', borderTop: '1px solid rgba(255,107,53,0.15)'}}>
            <div className="section-container">
              <div style={{textAlign: 'center', marginBottom: 40}}>
                <div className="intel-badge" style={{justifyContent: 'center', marginBottom: 12}}><Brain size={16} /> LATEST INTELLIGENCE REPORT</div>
                <h2 className="section-title" style={{marginTop: '12px'}}>Last Match Report</h2>
                <p className="section-subtitle">Download the latest match intelligence report in PDF format — completely free, no login required</p>
              </div>

              <div style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,107,53,0.2)',
                borderRadius: 12,
                padding: '32px',
                maxWidth: 600,
                margin: '0 auto',
              }}>
                <div style={{textAlign: 'center', marginBottom: 24}}>
                  <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 16}}>
                    <div style={{
                      width: 48, height: 48, borderRadius: '50%',
                      background: `${lastMatchReport.match.team1Info?.color || '#888'}22`,
                      border: `2px solid ${lastMatchReport.match.team1Info?.color || '#888'}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700, fontSize: '0.75rem', color: lastMatchReport.match.team1Info?.color || '#888',
                    }}>{lastMatchReport.match.team1}</div>
                    <span style={{fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-muted)'}}>vs</span>
                    <div style={{
                      width: 48, height: 48, borderRadius: '50%',
                      background: `${lastMatchReport.match.team2Info?.color || '#888'}22`,
                      border: `2px solid ${lastMatchReport.match.team2Info?.color || '#888'}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700, fontSize: '0.75rem', color: lastMatchReport.match.team2Info?.color || '#888',
                    }}>{lastMatchReport.match.team2}</div>
                  </div>
                  <h3 style={{fontSize: '1.3rem', fontWeight: 700, marginBottom: 8}}>Match {lastMatchReport.match.matchNo}</h3>
                  <p style={{color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: 4}}>
                    📍 {lastMatchReport.match.venue}
                  </p>
                  <p style={{color: 'var(--text-muted)', fontSize: '0.9rem'}}>
                    📅 {lastMatchReport.match.date} · 🕐 {lastMatchReport.match.time}
                  </p>
                </div>

                <div style={{
                  background: 'rgba(241,196,15,0.08)',
                  border: '1px solid rgba(241,196,15,0.2)',
                  borderRadius: 8,
                  padding: '14px 16px',
                  marginBottom: 24,
                  textAlign: 'center',
                }}>
                  <p style={{fontSize: '0.85rem', color: 'var(--text-muted)', margin: 0}}>
                    📊 Pre-match predictions, live updates & post-match analysis
                  </p>
                </div>

                <button
                  onClick={() => {
                    window.open(`/intelligence?matchNo=${lastMatchReport.match.matchNo}`, '_blank');
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 20px',
                    background: '#f1c40f',
                    color: '#111111',
                    border: '1px solid #f4cf56',
                    borderRadius: 6,
                    fontSize: '0.95rem',
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    transition: 'all 0.3s ease',
                  }}
                  onMouseEnter={e => e.target.style.background = '#f4cf56'}
                  onMouseLeave={e => e.target.style.background = '#f1c40f'}
                >
                  <Download size={18} />
                  Download Report PDF
                </button>

                <p style={{textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 14}}>
                  No login required · Free for everyone · High-resolution PDF
                </p>
              </div>
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className="cta-section">
          <div className="cta-container">
            <h2>Ready to Start Trading?</h2>
            <p>Join IPLTrade — completely free, no deposits, no charges. Just cricket, strategy and live market action.</p>
            <div className="cta-buttons">
              <a href={apkFile} download="IPLTrade.apk" className="btn-secondary" style={{width: '100%', justifyContent: 'center', textDecoration: 'none'}}>
                <Download size={20} /> Download IPLTrade App
              </a>
              <button onClick={handleLogin} className="btn-primary" style={{width: '100%', justifyContent: 'center'}}>
                Start Trading Free <ArrowRight size={20} />
              </button>
              <button onClick={handleLogin} className="btn-secondary" style={{width: '100%', justifyContent: 'center'}}>
                Login to Account
              </button>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="footer">
          <div className="footer-container">
            <div className="footer-grid">
              <div className="footer-col">
                <div className="navbar-logo" style={{marginBottom: '16px'}}>
                  IPL<span style={{color: '#fff'}}>Trade</span>
                </div>
                <p>India's first fantasy IPLTrade market gaming platform.</p>
              </div>
              <div className="footer-col">
                <h4>Platform</h4>
                <ul>
                  <li><a href="#features" onClick={e => { e.preventDefault(); scrollToSection('features'); }}><span className="footer-icon"><TrendingUp size={16} /></span> Features</a></li>
                  <li><a href="#how-it-works" onClick={e => { e.preventDefault(); scrollToSection('how-it-works'); }}><span className="footer-icon"><BarChart3 size={16} /></span> How It Works</a></li>
                  <li><a href="#faq" onClick={e => { e.preventDefault(); scrollToSection('faq'); }}><span className="footer-icon"><HelpCircle size={16} /></span> FAQ</a></li>
                  <li><a href="/market/leaderboard"><span className="footer-icon"><Trophy size={16} /></span> Leaderboard</a></li>
                </ul>
              </div>
              <div className="footer-col">
                <h4>Resources</h4>
                <ul>
                  <li><a href="/rules"><span className="footer-icon"><Gavel size={16} /></span> Rules &amp; Guidelines</a></li>
                  <li><a href="/declaration"><span className="footer-icon">📋</span> Terms &amp; Conditions</a></li>
                  <li><a href="/declaration"><span className="footer-icon">🔒</span> Privacy Policy</a></li>
                  <li><a href="/declaration"><span className="footer-icon">🎮</span> Responsible Gaming</a></li>
                </ul>
              </div>
              <div className="footer-col">
                <h4>About</h4>
                <ul>
                  <li><a href="/developer"><span className="footer-icon"><Code2 size={16} /></span> Developer</a></li>
                  <li><a href="mailto:support@ipltrade.in"><span className="footer-icon"><Mail size={16} /></span> Contact Us</a></li>
                  <li><a href="/declaration"><span className="footer-icon">🛡️</span> Privacy</a></li>
                </ul>
              </div>
            </div>

            <div className="footer-bottom">
              <div className="footer-links">
                <a href="/">Home</a>
                <a href="/rules">Rules</a>
                <a href="/declaration">Legal</a>
                <a href="/developer">Developer</a>
              </div>
              <div className="footer-copyright">
                © 2024 IPLTrade. All rights reserved. | <a href="/declaration" style={{color: '#ff6b35'}}>Terms</a> | <a href="/declaration" style={{color: '#ff6b35'}}>Privacy</a>
              </div>
              <div className="footer-disclaimer">Disclaimer: IPLTrade is a fantasy gaming platform. No real money involved.</div>
              <div className="footer-disclaimer">IPLTrade is an independent fan platform and is not affiliated with BCCI, the Indian Premier League, or any franchise/team.</div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

// FAQ Item Component
const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`faq-item${isOpen ? ' active' : ''}`}>
      <div className="faq-question" onClick={() => setIsOpen(!isOpen)}>
        <span className="faq-q-inner"><HelpCircle size={15} className="faq-help-icon" />{question}</span>
        <ChevronRight className="faq-icon" size={20} />
      </div>
      <div className="faq-answer">{answer}</div>
    </div>
  );
};

export default LandingPage;
